(() => {
  const ROOT_SELECTOR = '.jzv2-home';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const railStates = new Set();
  let resizeRaf = 0;
  let revealObserver = null;
  let hydrationObserver = null;
  let animationObserver = null;

  const getRevealObserver = () => {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
    return revealObserver;
  };

  const scheduleRailUpdate = (state) => {
    if (!state || state.raf) return;
    state.raf = requestAnimationFrame(() => {
      state.raf = 0;
      const { rail, prev, next } = state;
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const left = Math.max(0, rail.scrollLeft);
      const atStart = left <= 2;
      const atEnd = left >= maxScroll - 2;
      if (prev && prev.disabled !== atStart) prev.disabled = atStart;
      if (next && next.disabled !== atEnd) next.disabled = atEnd;
    });
  };

  const scheduleRailByElement = (rail) => {
    railStates.forEach((state) => {
      if (state.rail === rail) scheduleRailUpdate(state);
    });
  };

  const initRail = (shell) => {
    if (!shell || shell.dataset.jzv2RailReady === 'true') return;
    const rail = shell.querySelector('[data-jzv2-rail]');
    if (!rail) return;
    shell.dataset.jzv2RailReady = 'true';

    const prev = shell.querySelector('[data-jzv2-scroll-prev]');
    const next = shell.querySelector('[data-jzv2-scroll-next]');
    const state = { rail, prev, next, raf: 0 };
    railStates.add(state);

    const distance = () => Math.max(rail.clientWidth * 0.78, 280);
    prev?.addEventListener('click', () => {
      rail.scrollBy({ left: -distance(), behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    next?.addEventListener('click', () => {
      rail.scrollBy({ left: distance(), behavior: reducedMotion ? 'auto' : 'smooth' });
    });
    rail.addEventListener('scroll', () => scheduleRailUpdate(state), { passive: true });
    scheduleRailUpdate(state);
  };

  const initMoods = (moodRoot) => {
    if (!moodRoot || moodRoot.dataset.jzv2MoodReady === 'true') return;
    const tabs = [...moodRoot.querySelectorAll('[data-jzv2-mood-tab]')];
    const panels = [...moodRoot.querySelectorAll('[data-jzv2-mood-panel]')];
    if (!tabs.length || !panels.length) return;
    moodRoot.dataset.jzv2MoodReady = 'true';

    const activate = (activeTab, moveFocus = false) => {
      const target = activeTab.dataset.jzv2MoodTab;
      tabs.forEach((tab) => {
        const selected = tab === activeTab;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        const selected = panel.dataset.jzv2MoodPanel === target;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
      });
      if (moveFocus) activeTab.focus({ preventScroll: true });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(tabs[nextIndex], true);
        tabs[nextIndex].scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      });
    });
  };

  const initReviewNavigation = (root) => {
    const proof = root.querySelector('[data-jzv2-review-proof]');
    const reviews = root.querySelector('.jzv2-testimonials');
    if (!proof || !reviews || proof.dataset.jzv2ReviewNavFixed === 'true') return;
    proof.dataset.jzv2ReviewNavFixed = 'true';
    if (!reviews.id) reviews.id = `JzV2Testimonials-${root.dataset.sectionId || 'home'}`;
    proof.href = `#${reviews.id}`;
    proof.addEventListener('click', (event) => {
      event.preventDefault();
      reviews.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      try { history.replaceState(null, '', `#${reviews.id}`); } catch (error) {}
    });
  };

  const buildSearchUrl = (term, searchUrl) => {
    const match = window.location.pathname.match(/^\/(tr|de|fr|es|it)(?:\/|$)/i);
    const prefix = match ? `/${match[1].toLowerCase()}` : '';
    return `${searchUrl || `${prefix}/search`}?q=${encodeURIComponent(term)}&type=product`;
  };

  const hydrateColours = (section) => {
    const nav = section?.querySelector('.jzv2-theme-pills');
    if (!nav || nav.dataset.jzv2ColourReady === 'true') return;
    nav.dataset.jzv2ColourReady = 'true';
    const locale = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const words = {
      en: ['Shop wall art by colour', 'Neutral', 'Blue', 'Green', 'Red', 'Warm Tones', 'Black & White', 'Pastel', 'Colourful'],
      de: ['Wandkunst nach Farbe', 'Neutral', 'Blau', 'Grün', 'Rot', 'Warme Töne', 'Schwarz & Weiß', 'Pastell', 'Bunt'],
      fr: ['Art mural par couleur', 'Neutre', 'Bleu', 'Vert', 'Rouge', 'Tons chauds', 'Noir et blanc', 'Pastel', 'Multicolore'],
      es: ['Arte mural por color', 'Neutro', 'Azul', 'Verde', 'Rojo', 'Tonos cálidos', 'Blanco y negro', 'Pastel', 'Multicolor'],
      it: ['Arte da parete per colore', 'Neutro', 'Blu', 'Verde', 'Rosso', 'Toni caldi', 'Bianco e nero', 'Pastello', 'Multicolore'],
      tr: ['Rengine göre duvar sanatı', 'Nötr', 'Mavi', 'Yeşil', 'Kırmızı', 'Sıcak tonlar', 'Siyah ve beyaz', 'Pastel', 'Renkli']
    };
    const labels = words[locale] || words.en;
    nav.setAttribute('aria-label', labels[0]);
    // Quoted colour terms avoid broad "wall art" queries. No product data is changed.
    const colours = [
      ['neutral', labels[1], ['neutral', 'beige', 'cream']],
      ['blue', labels[2], ['blue']],
      ['green', labels[3], ['green']],
      ['red', labels[4], ['red']],
      ['warm', labels[5], ['orange', 'yellow', 'terracotta']],
      ['black-white', labels[6], ['black and white', 'black & white']],
      ['pastel', labels[7], ['pastel']],
      ['colourful', labels[8], ['colorful', 'colourful', 'multicolor', 'multicolour']]
    ];
    const fragment = document.createDocumentFragment();
    colours.forEach(([key, label, terms]) => {
      const link = document.createElement('a');
      const queryTerms = locale === 'en' ? terms : [...new Set([...terms, label.toLowerCase()])];
      const query = queryTerms.map((term) => `"${term}"`).join(' OR ');
      link.href = key === 'black-white' && nav.dataset.monochromeUrl
        ? nav.dataset.monochromeUrl
        : buildSearchUrl(query, nav.dataset.searchUrl);
      link.dataset.jzv2Colour = key;
      const dot = document.createElement('span');
      dot.className = 'jzv2-colour-dot';
      dot.setAttribute('aria-hidden', 'true');
      link.append(dot, document.createTextNode(label));
      fragment.appendChild(link);
    });
    nav.replaceChildren(fragment);
  };

  const extraPosts = [
    ['https://www.instagram.com/p/DbvJPk4iMzC/', 'https://cdn.shopify.com/s/files/1/0908/1248/1819/files/instagram-DbvJPk4iMzC.jpg?v=1788381341'],
    ['https://www.instagram.com/p/DbqtxAOob90/', 'https://cdn.shopify.com/s/files/1/0908/1248/1819/files/instagram-DbqtxAOob90.jpg?v=1788381358'],
    ['https://www.instagram.com/p/DaTZ4erCIEo/', 'https://cdn.shopify.com/s/files/1/0908/1248/1819/files/instagram-DaTZ4erCIEo.jpg?v=1788381370']
  ];

  const instagramIcon = '<span class="jzv2-social__post-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><rect x="3.5" y="3.5" width="17" height="17" rx="5" stroke="currentColor" stroke-width="1.4"/><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.4"/><circle cx="17.4" cy="6.7" r="1" fill="currentColor"/></svg></span>';

  const hydrateInstagram = (section) => {
    const feed = section?.querySelector('.jzv2-social__feed');
    if (!feed || feed.dataset.jzv2InstagramReady === 'true') return;
    feed.dataset.jzv2InstagramReady = 'true';
    section.querySelectorAll('.jzv2-social__cta').forEach((node) => node.remove());
    const fragment = document.createDocumentFragment();
    extraPosts.forEach(([href, src], index) => {
      const link = document.createElement('a');
      link.className = 'jzv2-social__post jzv2-social__post--extra';
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener';
      link.dataset.jzv2ExtraInstagram = String(index + 1);
      link.setAttribute('aria-label', 'View this Joffzart Instagram post');
      const image = document.createElement('img');
      image.src = src;
      image.alt = 'Joffzart Instagram post';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.fetchPriority = 'low';
      link.appendChild(image);
      link.insertAdjacentHTML('beforeend', instagramIcon);
      fragment.appendChild(link);
    });
    feed.appendChild(fragment);
    scheduleRailByElement(feed);
  };

  const getHydrationObserver = () => {
    if (hydrationObserver || !('IntersectionObserver' in window)) return hydrationObserver;
    hydrationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.target.classList.contains('jzv2-themes')) hydrateColours(entry.target);
        if (entry.target.classList.contains('jzv2-social')) hydrateInstagram(entry.target);
        hydrationObserver.unobserve(entry.target);
      });
    }, { rootMargin: '900px 0px 900px 0px', threshold: 0.01 });
    return hydrationObserver;
  };

  const getAnimationObserver = () => {
    if (animationObserver || !('IntersectionObserver' in window)) return animationObserver;
    animationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-animation-active', entry.isIntersecting && !reducedMotion);
      });
    }, { rootMargin: '120px 0px 120px 0px', threshold: 0.01 });
    return animationObserver;
  };

  const initRoot = (root) => {
    if (!root || root.dataset.jzv2RuntimeV21 === 'true') return;
    root.dataset.jzv2RuntimeV21 = 'true';
    root.classList.add('is-ready');

    const reveals = [...root.querySelectorAll('.jzv2-reveal')];
    reveals.forEach((item) => {
      if (item.closest('.jzv2-hero') || reducedMotion || !('IntersectionObserver' in window)) item.classList.add('is-visible');
      else getRevealObserver()?.observe(item);
    });

    root.querySelectorAll('.jzv2-rail-shell').forEach(initRail);
    root.querySelectorAll('[data-jzv2-moods]').forEach(initMoods);
    initReviewNavigation(root);

    const themes = root.querySelector('.jzv2-themes');
    const social = root.querySelector('.jzv2-social');
    if ('IntersectionObserver' in window) {
      if (themes) {
        getHydrationObserver()?.observe(themes);
        getAnimationObserver()?.observe(themes);
      }
      if (social) getHydrationObserver()?.observe(social);
    } else {
      hydrateColours(themes);
      hydrateInstagram(social);
      themes?.classList.add('is-animation-active');
    }
  };

  const start = () => document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  window.addEventListener('resize', () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      railStates.forEach(scheduleRailUpdate);
    });
  }, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR) || (event.target.matches?.(ROOT_SELECTOR) ? event.target : null);
    if (root) initRoot(root);
  });
})();
